/**
 * Prayer times, computed on device.
 *
 * v2 called the Aladhan API with a hand-rolled fallback its own author labelled
 * "Simplified… in a real app, use a proper library". Two problems with that
 * shape: a traveller most needs prayer times exactly when they have no data
 * (on a plane, on landing, on a foreign SIM), and it made the feature hostage to
 * a third party's uptime. So this implements the standard algorithm properly and
 * needs no network at all.
 *
 * Method follows the conventional solar model used by PrayTimes.org:
 *   Dhuhr   — solar transit
 *   Fajr    — sun at a given angle below the horizon before sunrise
 *   Isha    — sun at a given angle below the horizon after sunset (or a fixed
 *             interval after Maghrib, for Umm al-Qura)
 *   Asr     — shadow equal to object length + noon shadow (×2 for Hanafi)
 *   Maghrib — sunset
 */

export type PrayerName = 'fajr' | 'sunrise' | 'dhuhr' | 'asr' | 'maghrib' | 'isha';

export interface CalcMethod {
    id: string;
    name: string;
    fajrAngle: number;
    /** Either an angle below the horizon, or minutes after Maghrib. */
    isha: { angle: number } | { minutesAfterMaghrib: number };
}

export const METHODS: CalcMethod[] = [
    { id: 'MWL', name: 'Muslim World League', fajrAngle: 18, isha: { angle: 17 } },
    { id: 'ISNA', name: 'Islamic Society of North America', fajrAngle: 15, isha: { angle: 15 } },
    { id: 'EGYPT', name: 'Egyptian General Authority', fajrAngle: 19.5, isha: { angle: 17.5 } },
    { id: 'MAKKAH', name: 'Umm al-Qura, Makkah', fajrAngle: 18.5, isha: { minutesAfterMaghrib: 90 } },
    { id: 'KARACHI', name: 'University of Islamic Sciences, Karachi', fajrAngle: 18, isha: { angle: 18 } },
    { id: 'SINGAPORE', name: 'Majlis Ugama Islam Singapura', fajrAngle: 20, isha: { angle: 18 } },
    { id: 'FRANCE', name: 'Union des Organisations Islamiques de France', fajrAngle: 12, isha: { angle: 12 } },
];

/** Shafi'i/Maliki/Hanbali use factor 1; Hanafi uses 2. */
export type Madhab = 'standard' | 'hanafi';

const DEG = Math.PI / 180;
const sin = (d: number) => Math.sin(d * DEG);
const cos = (d: number) => Math.cos(d * DEG);
const tan = (d: number) => Math.tan(d * DEG);
const asin = (x: number) => Math.asin(x) / DEG;
const acos = (x: number) => Math.acos(x) / DEG;
const atan2 = (y: number, x: number) => Math.atan2(y, x) / DEG;
const fix = (a: number, n: number) => { const b = a - n * Math.floor(a / n); return b < 0 ? b + n : b; };

/** Julian Day for 00:00 UT on the given civil date. */
function julianDay(y: number, m: number, d: number): number {
    if (m <= 2) { y -= 1; m += 12; }
    const a = Math.floor(y / 100);
    const b = 2 - a + Math.floor(a / 4);
    return Math.floor(365.25 * (y + 4716)) + Math.floor(30.6001 * (m + 1)) + d + b - 1524.5;
}

/** Sun declination and equation of time (hours) for a Julian Day. */
function sunPosition(jd: number): { declination: number; equationOfTime: number } {
    const D = jd - 2451545.0;
    const g = fix(357.529 + 0.98560028 * D, 360);   // mean anomaly
    const q = fix(280.459 + 0.98564736 * D, 360);   // mean longitude
    const L = fix(q + 1.915 * sin(g) + 0.020 * sin(2 * g), 360); // apparent longitude
    const e = 23.439 - 0.00000036 * D;              // obliquity

    const declination = asin(sin(e) * sin(L));
    let RA = atan2(cos(e) * sin(L), cos(L)) / 15;   // right ascension, hours
    RA = fix(RA, 24);
    const equationOfTime = q / 15 - RA;

    return { declination, equationOfTime };
}

/**
 * Hour angle, in hours, for the sun to reach `angle` below the horizon.
 * Returns null in high latitudes where the sun never reaches that angle.
 */
function hourAngle(angle: number, latitude: number, declination: number): number | null {
    const x = (-sin(angle) - sin(latitude) * sin(declination)) / (cos(latitude) * cos(declination));
    if (x < -1 || x > 1) return null;
    return acos(x) / 15;
}

/**
 * Hour angle for Asr, when an object's shadow reaches `factor` × its length
 * plus the shadow it cast at noon.
 *
 * The resulting solar altitude is above the horizon, so the angle handed to
 * hourAngle() is negative — it shares the same "degrees below the horizon"
 * convention as Fajr and Isha. Negating it again put Asr at 22:49 in Hong Kong
 * and made it unsolvable at European latitudes.
 */
function asrHourAngle(factor: number, latitude: number, declination: number): number | null {
    const angle = -atan2(1, factor + tan(Math.abs(latitude - declination)));
    return hourAngle(angle, latitude, declination);
}

export interface PrayerTimes {
    /** Local Date objects for the given day. */
    times: Record<PrayerName, Date | null>;
    /** Whichever prayer is next, and how long until it. */
    next: { name: PrayerName; at: Date; inMs: number } | null;
    /** True where the sun never dips far enough for Fajr/Isha to be defined. */
    highLatitude: boolean;
}

/** Prayers proper — sunrise is shown for reference but is not a prayer. */
export const PRAYERS: PrayerName[] = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];

/**
 * Compute prayer times for a location and date, in the device's local timezone.
 */
export function prayerTimes(
    lat: number,
    lng: number,
    date: Date,
    method: CalcMethod,
    madhab: Madhab = 'standard',
): PrayerTimes {
    const jd = julianDay(date.getFullYear(), date.getMonth() + 1, date.getDate());
    // Refine for the location's longitude so the sun position matches solar noon.
    const { declination, equationOfTime } = sunPosition(jd - lng / (15 * 24));

    // Solar noon in UTC hours, then shifted to the device's local offset.
    const noonUTC = 12 - lng / 15 - equationOfTime;
    // getTimezoneOffset is minutes BEHIND UTC, so negate it.
    const tzHours = -date.getTimezoneOffset() / 60;

    const toDate = (utcHours: number | null): Date | null => {
        if (utcHours == null || !Number.isFinite(utcHours)) return null;
        const local = utcHours + tzHours;
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        d.setTime(d.getTime() + Math.round(local * 3_600_000));
        return d;
    };

    // 0.833° accounts for atmospheric refraction and the sun's radius.
    const sunriseHA = hourAngle(0.833, lat, declination);
    const fajrHA = hourAngle(method.fajrAngle, lat, declination);
    const asrHA = asrHourAngle(madhab === 'hanafi' ? 2 : 1, lat, declination);

    const dhuhr = toDate(noonUTC);
    const sunrise = toDate(sunriseHA == null ? null : noonUTC - sunriseHA);
    const maghrib = toDate(sunriseHA == null ? null : noonUTC + sunriseHA);
    const fajr = toDate(fajrHA == null ? null : noonUTC - fajrHA);
    const asr = toDate(asrHA == null ? null : noonUTC + asrHA);

    let isha: Date | null;
    if ('minutesAfterMaghrib' in method.isha) {
        isha = maghrib ? new Date(maghrib.getTime() + method.isha.minutesAfterMaghrib * 60_000) : null;
    } else {
        const ishaHA = hourAngle(method.isha.angle, lat, declination);
        isha = toDate(ishaHA == null ? null : noonUTC + ishaHA);
    }

    const times: Record<PrayerName, Date | null> = { fajr, sunrise, dhuhr, asr, maghrib, isha };

    const now = Date.now();
    const upcoming = PRAYERS
        .map((name) => ({ name, at: times[name] }))
        .filter((p): p is { name: PrayerName; at: Date } => p.at != null && p.at.getTime() > now)
        .sort((a, b) => a.at.getTime() - b.at.getTime())[0];

    return {
        times,
        next: upcoming ? { ...upcoming, inMs: upcoming.at.getTime() - now } : null,
        highLatitude: fajr == null || isha == null,
    };
}

/** "05:42" in the device's locale, 24-hour where that is the convention. */
export function formatTime(d: Date | null): string {
    if (!d) return '—';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * "in 3h 12m", translated. Takes the translator rather than importing it, so
 * this module stays pure and testable from the verify script.
 */
export function formatCountdown(
    ms: number,
    t: (k: 'prayer.imminent' | 'prayer.inMinutes' | 'prayer.inHours', v?: Record<string, string | number>) => string,
): string {
    const total = Math.max(0, Math.floor(ms / 60_000));
    const hours = Math.floor(total / 60);
    const minutes = total % 60;
    if (hours === 0) return minutes <= 1 ? t('prayer.imminent') : t('prayer.inMinutes', { minutes });
    return t('prayer.inHours', { hours, minutes });
}
